import EmptyList from '@/components/EmptyList';
import Filters, { orderByFilterEnum } from '@/components/Filter';
import Loader from '@/components/Loader';
import Tab from '@/components/Tab';
import Constants from '@/Constants';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import List, { ListRequest } from '@/models/List';
import { ApplicationState } from '@/store';
import { getListClear } from '@/store/list/action';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import ItemCard from './ItemCard';
import './style.scss';

const getQueryParams = ({
    applicationId,
    orgId,
    name,
    maxWorkload,
    mediaType,
    minWorkload,
    status,
    visibility,
    onlyHasProgramClass,
    orderBy,
    locale,
    moduleId,
    offset,
    limit,
}: ListRequest) => {
    let queryParams = `?withLessonAmount=true&withClassAmount=true&applicationId=${applicationId}&organizationId=${orgId}&onlyHasProgramClass=${onlyHasProgramClass}`;

    if (name) {
        queryParams += `&name=${name}`;
    }
    if (mediaType) {
        queryParams += `&mediaType=${mediaType}`;
    }
    if (minWorkload) {
        queryParams += `&minWorkload=${minWorkload}`;
    }
    if (maxWorkload) {
        queryParams += `&maxWorkload=${maxWorkload}`;
    }
    if (visibility) {
        queryParams += `&visibility=${visibility}`;
    }
    if (status) {
        queryParams += `&status=${status}`;
    }
    if (moduleId) {
        queryParams += `&moduleId=${moduleId}`;
    }
    if (orderBy) {
        queryParams += `&orderBy=${orderBy}`;
    }
    if (locale) {
        queryParams += `&locale=${locale}`;
    }
    if (_.isNumber(offset)) {
        queryParams += `&offset=${offset}`;
    }
    if (_.isNumber(limit)) {
        queryParams += `&limit=${limit}`;
    }

    return queryParams;
};

const Lists: FC = () => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const { refreshList } = useSelector((state: ApplicationState) => state.lists);

    const [searchName, setSearchName] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const [initialData, setInitialData] = useState(false);
    const [active, setActive] = useState(Constants.courseStatus.PUBLISHED);

    // filters
    const [slideRange, setSlideRange] = useState<number[]>([0, 90]);
    const [fakeSlideRange, setFakeSlideRange] = useState<number[]>([0, 90]);
    const [orderBy, setOrderBy] = useState(orderByFilterEnum.CREATED_AT);
    const [mediaType, setMediaType] = useState({ VIDEO: false, AUDIO: false, HTML: false, EXTERNAL: false });
    const [language, setLanguage] = useState({ pt: false, en: false, '': false });
    const [visibility, setVisibility] = useState({ public: false, private: false });
    const [showBottomLoading, setShowBottomLoading] = useState<boolean>(false);

    const history = useHistory();
    const dispatch = useDispatch();

    const getKey = (index: number) => {
        const {
            user: { orgId, applicationId },
        } = identity;

        let visibilityParam = undefined;

        if (visibility.private && !visibility.public) visibilityParam = '0';
        if (!visibility.private && visibility.public) visibilityParam = '1';

        let media: string | undefined = '';

        if (mediaType.AUDIO) media = 'AUDIO';
        if (mediaType.EXTERNAL) media += media.length > 0 ? ',EXTERNAL' : 'EXTERNAL';
        if (mediaType.HTML) media += media.length > 0 ? ',HTML' : 'HTML';
        if (mediaType.VIDEO) media += media.length > 0 ? ',VIDEO' : 'VIDEO';

        if (media.length <= 0) media = undefined;

        let locale: string | undefined = '';

        if (!language.pt && !language.en) locale = undefined;
        else if (language.pt && language.en) locale = undefined;
        else locale = language.pt ? 'pt' : 'en';

        const limit: number = 30;
        const offset: number = index * limit;

        const queryParams = getQueryParams({
            applicationId,
            orgId,
            name: searchName,
            minWorkload: slideRange[0].toString(),
            maxWorkload: slideRange[1] < 90 ? slideRange[1].toString() : undefined,
            status: active.toUpperCase(),
            visibility: visibilityParam,
            orderBy: orderBy === orderByFilterEnum.ALPHABETICAL ? undefined : orderBy,
            mediaType: media,
            locale,
            onlyHasProgramClass: 0,
            moduleId: Constants.PLAYLIST_MODULE_ID,
            offset,
            limit,
        });

        return Constants.LMS_COURSES_URI + queryParams;
    };

    const { data: lists, loading, total, loadMore, revalidateResult } = useGetPaginated<List>(getKey, { revalidateOnMount: true });

    const callback = useCallback(() => {
        if (!loading && total && lists.length < total) {
            setShowBottomLoading(true);
            loadMore();
        } else if (!loading) setShowBottomLoading(false);
    }, [lists, searchName]);

    useBottomScrollListener(callback, { offset: 150 });

    useEffect(() => {
        if (!loading) setInitialLoading(false);
        if (lists && lists.length > 0) setInitialData(true);
    }, [loading, lists]);

    useEffect(() => {
        dispatch(getListClear());
    }, [active]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchName(e.target.value);
    };

    useEffect(() => {
        if (refreshList) {
            revalidateResult();
        }
    }, [refreshList]);

    const handleNewList = (): void => {
        history.push('/lists/create');
    };

    const renderEmpty = () => !loading && lists && lists.length <= 0;

    return (
        <>
            {initialLoading && <Loader />}
            {!initialLoading && (
                <div className="lists">
                    <div className="list">
                        <div className="d-flex top-content">
                            <button className="btn btn-primary btn-sm new-item-button" onClick={handleNewList} type="button">
                                {strings.CREATE_NEW_PLAYLIST}
                            </button>
                            <div className="form-group inner-addon left-addon">
                                <i className="material-icons-outlined align-middle input-icon">search</i>
                                <input
                                    autoComplete="nope"
                                    className="form-control"
                                    onChange={handleSearch}
                                    placeholder={strings.SEARCH}
                                    type="search"
                                />
                            </div>
                        </div>

                        <Filters
                            entityType={Constants.entityTypes.COURSE}
                            fakeSlideRange={fakeSlideRange}
                            language={language}
                            mediaType={mediaType}
                            orderBy={orderBy}
                            setFakeSlideRange={setFakeSlideRange}
                            setLanguage={setLanguage}
                            setMediaType={setMediaType}
                            setOrderBy={setOrderBy}
                            setSlideRange={setSlideRange}
                            setVisibility={setVisibility}
                            slideRange={slideRange}
                            status={active}
                            visibility={visibility}
                        />

                        <div className="tabs-container">
                            <Tab
                                active={active === Constants.courseStatus.PUBLISHED}
                                icon="outlined_flag"
                                setActive={() => setActive(Constants.courseStatus.PUBLISHED)}
                                title={strings.PUBLISHED}
                            />
                            <Tab
                                active={active === Constants.courseStatus.DRAFT}
                                icon="gesture"
                                setActive={() => setActive(Constants.courseStatus.DRAFT)}
                                title={strings.DRAFT}
                            />
                            <Tab
                                active={active === Constants.courseStatus.ARCHIVED}
                                icon="how_to_vote"
                                setActive={() => setActive(Constants.courseStatus.ARCHIVED)}
                                title={strings.ARCHIVED}
                            />
                        </div>
                        <div className="list-container">
                            {loading && !showBottomLoading ? (
                                <Loader />
                            ) : (
                                lists &&
                                (lists.length > 0 || searchName !== '' || initialData) && (
                                    <div className="list-lists">{lists && lists.map((item: List) => <ItemCard item={item} key={item.id} />)}</div>
                                )
                            )}
                            {renderEmpty() && (
                                <EmptyList
                                    buttonTitle={strings.CREATE_NEW_ITEM}
                                    handleNewList={handleNewList}
                                    icon={Constants.icons.BALLOT}
                                    title={strings.EMPTY_LIST}
                                />
                            )}
                        </div>
                        {showBottomLoading && <Loader />}
                    </div>
                </div>
            )}
        </>
    );
};

export default Lists;
